import {motion} from "framer-motion";
import {Clock, MapPin, Smartphone, UserSquare2Icon, ChevronRight} from "lucide-react";
import React from "react";

export interface EmployeeWeeklyRow {
    userId: string;
    userName: string;
    days: Record<string, number>;
    totalHours: string;
}

interface EmployeeCardProps {
    employee: EmployeeWeeklyRow;
    weekDays: Date[];
    onClick?: () => void;
    isSelected?: boolean;
}

interface EmployeeTabletCardProps {
    employee: any;
    onClick: () => void;
    formatTime: (value: string | null) => string;
    formatHours: (value: number) => string;
}


export const EmployeeMobileCard = ({
                                       employee,
                                       weekDays,
                                       onClick,
                                       isSelected = false,
                                   }: EmployeeCardProps) => {
    return (
        <motion.button
            type="button"
            whileTap={{ scale: 0.98 }}
            onClick={onClick}
            className={`w-full text-left rounded-2xl border p-4 transition ${
                isSelected
                    ? "border-blue-500 bg-blue-500/10"
                    : "border-gray-800 bg-[#151515] hover:border-gray-700"
            }`}
        >
            {/* Employee Header */}
            <div className="flex items-center justify-between gap-3">
                <div className="flex min-w-0 items-center gap-3">
                    <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-purple-600 text-white">
                        {employee.userName
                            ?.charAt(0)
                            .toUpperCase()}
                    </div>

                    <div className="min-w-0">
                        <p className="truncate font-semibold text-white">
                            {employee.userName}
                        </p>

                        <p className="text-xs text-gray-500">
                            Employee
                        </p>
                    </div>
                </div>

                <StatusBadge
                    status={employee.totalHours}
                />
            </div>

            {/* Total Hours */}
            <div className="mt-4 rounded-xl bg-black/20 p-3">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 text-gray-400">
                        <Clock size={16} />
                        <span className="text-sm">
                            Weekly Hours
                        </span>
                    </div>

                    <span className="text-lg font-bold text-white">
                        {formatTime(employee.totalHours)}
                    </span>
                </div>
            </div>

            {/* Daily Hours */}
            <div className="mt-4 grid grid-cols-3 gap-2">
                {weekDays.map((day) => {
                    const dateKey =
                        day.toISOString().split("T")[0];

                    const hours =
                        employee.days[dateKey] ?? 0;

                    return (
                        <div
                            key={dateKey}
                            className="rounded-lg bg-[#1c1c1c] p-2 text-center"
                        >
                            <p className="text-[10px] text-gray-500">
                                {day.toLocaleDateString(
                                    "en-US",
                                    {
                                        weekday: "short",
                                    }
                                )}
                            </p>

                            <p className="mt-1 text-sm font-semibold text-white">
                                {hours.toFixed(1)}h
                            </p>
                        </div>
                    );
                })}
            </div>

            <div className="mt-4 text-center text-xs text-blue-400">
                Tap to view details
            </div>
        </motion.button>
    );
};

export function EmployeeTabletCard({
                                               employee,
                                               onClick,
                                               formatTime,
                                               formatHours,
                                           }: EmployeeTabletCardProps) {
    const isWorking = employee.status === "Working";

    return (
        <button
            type="button"
            onClick={onClick}
            className="
                w-full
                text-left
                rounded-2xl
                border
                border-gray-800
                bg-[#101010]
                p-4
                transition
                hover:border-gray-700
                active:scale-[0.99]
            "
        >

            {/* Top */}
            <div className="flex items-center justify-between gap-4">

                {/* Employee */}
                <div className="flex min-w-0 items-center gap-3">

                    <div
                        className="
                            h-12
                            w-12
                            shrink-0
                            rounded-full
                            bg-gradient-to-r
                            from-purple-600
                            to-blue-500
                            flex
                            items-center
                            justify-center
                            text-white
                            font-semibold
                        "
                    >
                        {employee?.userName
                            ?.split(" ")
                            ?.map((name: string) => name[0])
                            ?.slice(0, 2)
                            ?.join("")
                            ?.toUpperCase()}
                    </div>

                    <div className="min-w-0">

                        <p className="truncate font-semibold text-white">
                            {employee.userName}
                        </p>

                        <p className="truncate text-xs text-gray-500">
                            {employee.employeeNumber}
                        </p>

                    </div>

                </div>

                {/* Status */}
                <span
                    className={`
                        shrink-0
                        rounded-full
                        border
                        px-3
                        py-1
                        text-xs
                        font-medium

                        ${
                        isWorking
                            ? "border-green-500/20 bg-green-500/10 text-green-400"
                            : "border-gray-500/20 bg-gray-500/10 text-gray-400"
                    }
                    `}
                >
                    {isWorking
                        ? "Working"
                        : "Off"}
                </span>

            </div>

            {/* Project */}
            <div className="mt-4">

                <p className="text-xs text-gray-500">
                    Project
                </p>

                <p className="mt-1 truncate text-sm text-gray-200">
                    {employee.projectName ?? "No Project"}
                </p>

            </div>

            {/* Attendance Grid */}
            <div className="mt-4 grid grid-cols-4 gap-2">

                <div className="rounded-xl bg-[#181818] p-3">

                    <p className="text-[10px] text-gray-500">
                        Sign In
                    </p>

                    <p className="mt-1 text-sm text-white">
                        {formatTime(
                            employee.signInTime
                        )}
                    </p>

                </div>

                <div className="rounded-xl bg-[#181818] p-3">

                    <p className="text-[10px] text-gray-500">
                        Hours
                    </p>

                    <p className="mt-1 text-sm font-semibold text-white">
                        {formatHours(
                            Number(employee.totalHours ?? 0)
                        )}
                    </p>

                </div>

                <div className="rounded-xl bg-[#181818] p-3">

                    <p className="text-[10px] text-gray-500">
                        Site
                    </p>

                    <p className="mt-1 truncate text-sm text-white">
                        {employee.siteCode ?? "—"}
                    </p>

                </div>

                <div className="rounded-xl bg-[#181818] p-3">

                    <p className="text-[10px] text-gray-500">
                        Device
                    </p>

                    <p className="mt-1 truncate text-sm text-white">
                        {employee.deviceId ?? "—"}
                    </p>

                </div>

            </div>

            {/* Footer */}
            <div className="mt-4 flex items-center justify-between border-t border-gray-800 pt-3">

                <div className="flex items-center gap-2 text-gray-500">

                    <MapPin size={14} />

                    <span className="text-xs">
                        {employee.siteCode ?? "No site"}
                    </span>

                </div>

                <div className="flex items-center gap-1 text-xs text-blue-400">
                    View Employee
                    <ChevronRight size={15} />
                </div>

            </div>

        </button>
    );
}



function DetailItem({
                        label,
                        value,
                        icon: Icon,
                    }: {
    label: string;
    value: string;
    icon?: any;
}) {

    return (

        <div>

            <div className="
    flex
    items-center
    gap-1.5
    ">

    {Icon && (
        <Icon
            size={13}
        className="text-gray-500"
            />
    )}

    <p className="
    text-[10px]
    uppercase
    tracking-wide
    text-gray-500
    ">
    {label}
    </p>

    </div>


    <p className="
    mt-1

    break-words

    text-sm
    text-gray-200
    ">
    {value}
    </p>

    </div>
);
}


function formatTime(
    value: string | null
) {

    if (!value) {
        return "—";
    }

    return new Date(
        value
    ).toLocaleTimeString(
        "en-US",
        {
            timeStyle: "short",
        }
    );
}
function formatMobileDate(
    dateString: string
) {

    const [
        year,
        month,
        day,
    ] =
        dateString
            .split("-")
            .map(Number);

    return new Date(
        year,
        month - 1,
        day
    ).toLocaleDateString(
        "en-US",
        {
            month: "short",
            day: "numeric",
        }
    );
}

function SelectedDayCard({
                             day,
                             onClose,
                         }: {
    day: any;
    onClose: () => void;
}) {

    return (

        <motion.section

            initial={{
        opacity: 0,
            y: 15,
    }}

    animate={{
        opacity: 1,
            y: 0,
    }}

    className="
    mt-5

    rounded-xl

    border
    border-gray-800

    bg-[#101010]

    overflow-hidden
    "
    >


    {/* HEADER */}

    <div className="
    flex
    items-center
    justify-between

    gap-3

    border-b
    border-gray-800

    p-4
    ">

    <div>

    <h2 className="
    text-base
    font-semibold
    ">
    {day.day}
    </h2>

    <p className="
    mt-1
    text-xs
    text-gray-500
    ">
    Detailed Work Session
    </p>

    </div>


    <button
    onClick={onClose}

    className="
    min-h-[40px]
    rounded-lg

    px-3

    text-xs

    text-gray-400

    hover:bg-gray-800
    hover:text-white
    "
    >
    Close
    </button>

    </div>


    {/* DETAILS */}

    <div className="
    grid

    grid-cols-1
    sm:grid-cols-2
    md:grid-cols-3
    lg:grid-cols-4

    gap-4

    p-4
    ">


    <DetailItem
    label="Project"
    value={
        day.projectName ??
            "—"
    }
    />


    <DetailItem
    label="Sign In"
    value={
        formatTime(
            day.signInTime
)
}
    />


    <DetailItem
    label="Sign Out"
    value={
        formatTime(
            day.signOutTime
)
}
    />


    <DetailItem
    label="Total"
    value={`${day.totalHours.toFixed(2)} hrs`}
    />


    <DetailItem
    label="Site"
    value={
        day.siteCode ??
            "—"
    }
    icon={MapPin}
    />


    <DetailItem
    label="Device"
    value={
        day.deviceId ??
            "—"
    }
    icon={Smartphone}
    />

    </div>

    </motion.section>
);
}


function StatusBadge({
                         status,
                     }: {
    status: string;
}) {

    const completed =
        status === "Completed";

    return (

        <span className={`
            inline-flex
            whitespace-nowrap

            rounded-full

            border

            px-2
            py-1

            text-[10px]
            font-medium

            ${
            completed
                ? `
                        border-green-500/20
                        bg-green-500/10
                        text-green-400
                    `
                : `
                        border-gray-500/20
                        bg-gray-500/10
                        text-gray-500
                    `
        }
        `}>
    {status}
    </span>
);
}

function MobileValue({
                         label,
                         value,
                     }: {
    label: string;
    value: string;
}) {

    return (

        <div className="
    rounded-lg
    bg-gray-900
    p-2.5
    ">
    <p className="
    text-[9px]
    uppercase
    tracking-wide
    text-gray-500
    ">
    {label}
    </p>
    <p className="
    mt-1
    truncate
    text-xs
    font-medium
    text-gray-200
    ">
    {value}
    </p>
    </div>
);
}

function TableHeader({
                         children,
                     }: {
    children: React.ReactNode;
}) {

    return (

        <th className="
    whitespace-nowrap

    p-3

    text-[10px]

    font-semibold

    uppercase

    tracking-wide

    text-gray-500
    ">
    {children}
    </th>
);
}

function MobileDayCard({
                           day,
                           onSelect,
                       }: {
    day: any;
    onSelect: () => void;
}) {

    const hasWork =
        Boolean(day.projectName);


    return (

        <button
            type="button"
    onClick={onSelect}

    className="
    w-full
    text-left

    rounded-xl

    border
    border-gray-800

    bg-[#101010]

    p-4

    transition

    active:scale-[0.99]

    hover:border-gray-700
    "
    >

    {/* TOP */}

    <div className="
    flex
    items-start
    justify-between
    gap-3
    ">


    <div>

    <p className="
    text-sm
    font-semibold
    text-white
    ">
    {day.day}
    </p>

    <p className="
    mt-0.5
    text-xs
    text-gray-500
    ">
    {formatMobileDate(day.date)}
    </p>

    </div>


    <StatusBadge
    status={day.status}
    />

    </div>


    {/* PROJECT */}

    <div className="
    mt-4
    border-t
    border-gray-800
    pt-3
    ">

    <p className="
    text-[10px]
    uppercase
    tracking-wide
    text-gray-500
    ">
    Project
    </p>

    <p className="
    mt-1
    truncate
    text-sm
    font-medium
    text-gray-200
    ">

    {day.projectName ??
    "No work recorded"}

    </p>


    {day.projectNumber && (

        <p className="
        mt-0.5
        text-xs
        text-gray-500
        ">
        {day.projectNumber}
        </p>

    )}

    </div>


    {/* TIMES */}

    {hasWork && (

        <div className="
        mt-4

        grid
        grid-cols-3

        gap-2
        ">
        <MobileValue
        label="Sign In"
        value={
            formatTime(
                day.signInTime
    )
    }
        />
        <MobileValue
        label="Sign Out"
        value={
            formatTime(
                day.signOutTime
    )
    }
        />
        <MobileValue
        label="Total"
        value={`${day.totalHours.toFixed(1)}h`}
        />

        </div>

    )}

    </button>
);
}

function InfoItem({
                      label,
                      value,
                  }: {
    label: string;
    value: string;
}) {

    return (

        <div>
            <p className="
    text-[10px]
    uppercase
    tracking-wider
    text-gray-500
    ">
    {label}
    </p>

    <p className="
    mt-1
    text-sm
    text-gray-200
    ">
    {value}
    </p>
    </div>
);
}

function ResponsiveSummaryCard({
                                   icon: Icon,
                                   label,
                                   value,
                               }: {
    icon: any;
    label: string;
    value: string;
}) {

    return (

        <div className="
    min-w-0
    rounded-xl
    border
    border-gray-800

    bg-[#101010]
    p-3
    sm:p-4">
    <div className="
    flex
    items-center
    gap-2
    ">
    <Icon
    size={16}
    className="
    shrink-0
    text-blue-400"
    />
    <p className="
    truncate
    text-[10px]
    sm:text-xs
    uppercase
    tracking-wide
    text-gray-500
    ">
    {label}
    </p>

    </div>

    <p className="
    mt-2
    text-base
    sm:text-lg
    md:text-xl
    font-bold
    text-white
    ">
    {value}
    </p>
    </div>
);

    function EmptyState() {

        return (

            <div className="
            rounded-xl

            border
            border-gray-800

            bg-[#101010]

            p-8

            text-center
        ">

                <UserSquare2Icon
                    size={32}
                    className="
                    mx-auto
                    text-gray-600
                "
                />

                <p className="
                mt-3

                text-sm

                text-gray-500
            ">
                    No employee work hours found.
                </p>

            </div>
        );
    }


}

export const EmployeeTimeSheetHelper = ({
    DetailItem,
    formatTime,
    formatMobileDate,
    SelectedDayCard,
    StatusBadge,
    MobileValue,
    ResponsiveSummaryCard,
    InfoItem,
    TableHeader,
    MobileDayCard,
})